import { generateHTML, type JSONContent } from '@tiptap/react';
import { Base64 } from 'js-base64';
import Quill from 'quill';
import 'quill-mention';
import { generatePlainText } from 'src/app/project/components/input/rich-text/generate-plain-text';
import { richTextFormats } from 'src/app/project/components/input/rich-text/rich-text-formats';
import { createPointFieldExtensions } from './richTextExtensions';

type TMention = {
  id: string;
  value: string;
  denotationChar: string;
};

type TStoredFragment = {
  insert: string | { mention: TMention };
};

type TStoredValue = {
  ops: TStoredFragment[];
};

const MENTION_CHAR = '@';
const BLOCKS = 'p, h1, h2, h3, h4, h5, h6, li, blockquote';
const EDITOR_MENTION = 'span[data-type="mention"]';
const STORED_MENTION = 'span.mention';
const CONVERTER_MODULES = { clipboard: { matchVisual: false } };
const RICH_TEXT_EXTENSIONS = createPointFieldExtensions();

const withConverter = <T>(read: (converter: Quill) => T): T => {
  const container = document.createElement('div');

  document.body.appendChild(container);

  try {
    return read(new Quill(container, { formats: richTextFormats, modules: CONVERTER_MODULES }));
  } finally {
    container.remove();
  }
};

const parseStoredValue = (value: string): TStoredValue | null => {
  try {
    const stored = JSON.parse(Base64.decode(value));

    return Array.isArray(stored?.ops) ? stored : null;
  } catch (error) {
    return null;
  }
};

const createMentionElement = (element: Element): Element => {
  const mention = element.ownerDocument.createElement('span');

  mention.setAttribute('data-type', 'mention');
  mention.setAttribute('class', 'mention');
  mention.setAttribute('data-id', element.getAttribute('data-id') ?? '');
  mention.setAttribute('data-label', element.getAttribute('data-value') ?? '');
  mention.textContent = `${MENTION_CHAR}${element.getAttribute('data-value') ?? ''}`;

  return mention;
};

const rewriteMentionsForEditor = (root: Element): void => {
  Array.from(root.querySelectorAll(STORED_MENTION)).forEach((element) =>
    element.replaceWith(createMentionElement(element)),
  );
};

const toStoredMention = (element: Element): Element => {
  const mention = element.ownerDocument.createElement('span');
  const label = element.getAttribute('data-label') ?? '';

  mention.setAttribute('class', 'mention');
  mention.setAttribute('data-id', element.getAttribute('data-id') ?? '');
  mention.setAttribute('data-value', label);
  mention.setAttribute('data-denotation-char', MENTION_CHAR);
  mention.textContent = `${MENTION_CHAR}${label}`;

  return mention;
};

const keepBlankLines = (body: Element): void => {
  Array.from(body.querySelectorAll(BLOCKS))
    .filter((block) => block.childNodes.length === 0)
    .forEach((block) => block.appendChild(body.ownerDocument.createElement('br')));
};

const rewriteMentionsForStorage = (body: Element): void => {
  Array.from(body.querySelectorAll(EDITOR_MENTION)).forEach((element) =>
    element.replaceWith(toStoredMention(element)),
  );
};

const toStorageHtml = (html: string): string => {
  const parsed = new DOMParser().parseFromString(html, 'text/html');

  keepBlankLines(parsed.body);
  rewriteMentionsForStorage(parsed.body);

  return parsed.body.innerHTML;
};

export const decodeRichTextValue = (value: string): string => {
  const stored = parseStoredValue(value);

  if (!stored) {
    return '';
  }

  return withConverter((converter) => {
    converter.setContents(stored);
    rewriteMentionsForEditor(converter.root);

    return converter.root.innerHTML;
  });
};

const toMentionId = (fragment: TStoredFragment): string | null =>
  typeof fragment.insert === 'string' ? null : fragment.insert.mention.id;

export const readMentionIds = (value: string): string[] => {
  const stored = parseStoredValue(value);

  if (!stored) {
    return [];
  }

  return stored.ops.map(toMentionId).filter((id): id is string => id !== null);
};

const hasContent = (fragments: TStoredFragment[]): boolean =>
  generatePlainText({ ops: fragments }).trim() !== '';

export const encodeRichTextValue = (html: string): string => {
  const fragments = withConverter((converter) => {
    converter.setContents(converter.clipboard.convert(toStorageHtml(html)));

    return converter.getContents().ops as TStoredFragment[];
  });

  if (!hasContent(fragments)) {
    return '';
  }

  return Base64.encode(JSON.stringify({ ops: fragments }));
};

export const encodeRichTextDocument = (content: JSONContent): string =>
  encodeRichTextValue(generateHTML(content, RICH_TEXT_EXTENSIONS));
